
import { useState, useEffect } from 'react'
import {
  VStack,
  HStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Button,
  Switch,
  Grid,
  Spinner,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react'
import { SettingsService, SecuritySettingsData } from '@/lib/services/settingsService'
import { useToastNotification } from '@/lib/utils/useToastNotification'

interface SecuritySettingsFormProps {
  onSuccess?: () => void
}

export function SecuritySettingsForm({ onSuccess }: SecuritySettingsFormProps) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState<SecuritySettingsData | null>(null)
  const { showError, showSuccess } = useToastNotification()

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response = await SettingsService.getSecuritySettings()
      
      if (response.success && response.data) {
        const loadedSettings: SecuritySettingsData = {
          two_factor: {
            enabled: response.data.two_factor?.enabled ?? false,
            required: response.data.two_factor?.required ?? false,
            backup_codes_count: response.data.two_factor?.backup_codes_count ?? 10,
          },
          session: {
            session_enabled: response.data.session?.session_enabled ?? true,
            session_timeout: response.data.session?.session_timeout ?? 30,
            max_login_attempts: response.data.session?.max_login_attempts ?? 5,
            lockout_duration: response.data.session?.lockout_duration ?? 15,
          },
        }
        setSettings(loadedSettings)
      } else {
        const defaultSettings: SecuritySettingsData = {
          two_factor: {
            enabled: false,
            required: false,
            backup_codes_count: 10,
          },
          session: {
            session_enabled: true,
            session_timeout: 30,
            max_login_attempts: 5,
            lockout_duration: 15,
          },
        }
        setSettings(defaultSettings)
      }
    } catch (err: any) {
      showError({ message: err.message || 'Failed to load security settings' })
      const defaultSettings: SecuritySettingsData = {
        two_factor: {
          enabled: false,
          required: false,
          backup_codes_count: 10,
        },
        session: {
          session_enabled: true,
          session_timeout: 30,
          max_login_attempts: 5,
          lockout_duration: 15,
        },
      }
      setSettings(defaultSettings)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!settings) return
    
    try {
      setSaving(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      
      const response = await SettingsService.updateSecuritySettings(settings)
      if (response.success) {
        showSuccess('Success', response.message || 'Security settings updated successfully')
        await loadSettings()
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }, 100)
        onSuccess?.()
      } else {
        throw new Error(response.message || 'Failed to update security settings')
      }
    } catch (err: any) {
      showError(err)
    } finally {
      setSaving(false)
    }
  }

  const updateSetting = (section: keyof SecuritySettingsData, key: string, value: any) => {
    if (!settings) return
    
    setSettings((prev) => {
      if (!prev) return null
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [key]: value,
        },
      }
    })
  }

  if (loading || !settings) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading security settings...</Text>
      </VStack>
    )
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Two-Factor Authentication Settings */}
      <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
        <FormLabel pt={2} fontWeight="medium">
          2FA Enabled
        </FormLabel>
        <FormControl>
          <Switch
            isChecked={settings.two_factor.enabled}
            onChange={(e) => updateSetting('two_factor', 'enabled', e.target.checked)}
          />
          <FormHelperText>Enable two-factor authentication for all users</FormHelperText>
        </FormControl>
      </Grid>

      <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
        <FormLabel pt={2} fontWeight="medium">
          2FA Required
        </FormLabel>
        <FormControl>
          <Switch
            isChecked={settings.two_factor.required}
            onChange={(e) => updateSetting('two_factor', 'required', e.target.checked)}
            isDisabled={!settings.two_factor.enabled}
          />
          <FormHelperText>Require two-factor authentication for all users (only applies if 2FA is enabled)</FormHelperText>
        </FormControl>
      </Grid>

      <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
        <FormLabel pt={2} fontWeight="medium">
          Backup Codes Count
        </FormLabel>
        <FormControl>
          <NumberInput
            value={settings.two_factor.backup_codes_count}
            onChange={(_, value) => updateSetting('two_factor', 'backup_codes_count', isNaN(value) ? 10 : value)}
            min={5}
            max={20}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <FormHelperText>Number of backup codes to generate for each user (5-20)</FormHelperText>
        </FormControl>
      </Grid>

      {/* Session Settings */}
      <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
        <FormLabel pt={2} fontWeight="medium">
          Enable Session Timeout
        </FormLabel>
        <FormControl>
          <Switch
            isChecked={settings.session.session_enabled}
            onChange={(e) => updateSetting('session', 'session_enabled', e.target.checked)}
          />
          <FormHelperText>Enable session timeout. When disabled, sessions will remain active indefinitely (useful for systems that need to stay always active)</FormHelperText>
        </FormControl>
      </Grid>

      <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
        <FormLabel pt={2} fontWeight="medium">
          Session Timeout (minutes)
        </FormLabel>
        <FormControl>
          <NumberInput
            value={settings.session.session_timeout}
            onChange={(_, value) => updateSetting('session', 'session_timeout', isNaN(value) ? 30 : value)}
            min={5}
            max={1440}
            isDisabled={!settings.session.session_enabled}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <FormHelperText>Session timeout in minutes (5-1440 minutes / 1 day). Only applies when session timeout is enabled.</FormHelperText>
        </FormControl>
      </Grid>

      <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
        <FormLabel pt={2} fontWeight="medium">
          Max Login Attempts
        </FormLabel>
        <FormControl>
          <NumberInput
            value={settings.session.max_login_attempts}
            onChange={(_, value) => updateSetting('session', 'max_login_attempts', isNaN(value) ? 5 : value)}
            min={3}
            max={10}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <FormHelperText>Maximum number of failed login attempts before account lockout (3-10)</FormHelperText>
        </FormControl>
      </Grid>

      <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
        <FormLabel pt={2} fontWeight="medium">
          Lockout Duration (minutes)
        </FormLabel>
        <FormControl>
          <NumberInput
            value={settings.session.lockout_duration}
            onChange={(_, value) => updateSetting('session', 'lockout_duration', isNaN(value) ? 15 : value)}
            min={5}
            max={60}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <FormHelperText>Account lockout duration in minutes after max login attempts (5-60 minutes)</FormHelperText>
        </FormControl>
      </Grid>

      {/* Action Buttons */}
      <HStack spacing={4} justify="flex-end" pt={4}>
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={saving}
          loadingText="Saving..."
        >
          Save Settings
        </Button>
      </HStack>
    </VStack>
  )
}

